import { Link } from "react-router-dom";
import useScrollReveal from "../../hooks/useScrollReveal";

const HeroSection = ({ heroImage, heroPosition }) => {
  const textReveal = useScrollReveal({ threshold: 0.1 });
  const coconReveal = useScrollReveal({ threshold: 0.2 });
  const choiceReveal = useScrollReveal({ threshold: 0.15 });
  const mantreReveal = useScrollReveal({ threshold: 0.2 });

  return (
    <>
      {/* IMAGE PURE — aucun texte */}
      <section className="relative h-[85vh] sm:h-screen overflow-hidden">
        <div
          className="absolute inset-0 bg-cover"
          style={{ backgroundImage: `url(${heroImage})`, backgroundPosition: `${heroPosition.x}% ${heroPosition.y}%` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#E8D2B5]/60" />
      </section>

      {/* TEXTE — fond crème chaud */}
      <section className="px-6 py-24 sm:py-36 bg-[#E8D2B5]" data-testid="hero-section">
        <div className="max-w-2xl mx-auto text-center">

          {/* Ouverture poétique */}
          <div
            ref={textReveal.ref}
            className={`mb-20 space-y-3 scroll-reveal-slow ${textReveal.isVisible ? "visible" : ""}`}
          >
            <p className="font-serif text-base sm:text-lg text-[#3F2A24]/60 leading-[2.2]">
              Il y a des moments
            </p>
            <p className="font-serif text-base sm:text-lg text-[#3F2A24]/60 leading-[2.2]">
              où quelque chose s'ouvre.
            </p>
            <p className="font-serif text-base sm:text-lg text-[#3F2A24]/60 italic leading-[2.2] pt-4">
              Sans bruit.
            </p>
            <p className="font-serif text-base sm:text-lg text-[#3F2A24]/60 italic leading-[2.2]">
              Sans explication.
            </p>
          </div>

          {/* COCON */}
          <div
            ref={coconReveal.ref}
            className={`mb-20 scroll-reveal-slow ${coconReveal.isVisible ? "visible" : ""}`}
          >
            <h1
              className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#3F2A24] tracking-[0.4em] uppercase"
              data-testid="hero-title"
            >
              COCON
            </h1>
          </div>

          {/* Deux chemins */}
          <div
            ref={choiceReveal.ref}
            className={`mb-24 scroll-reveal ${choiceReveal.isVisible ? "visible" : ""}`}
          >
            <p className="font-serif text-base sm:text-lg text-[#3F2A24]/50 mb-4">
              Deux chemins.
            </p>
            <p className="font-serif text-base sm:text-lg text-[#3F2A24]/50 mb-12">
              Deux rythmes.
            </p>

            <div className="w-8 h-[1px] bg-[#C5A059]/40 mx-auto mb-12" />

            <div className="flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-8">
              <Link
                to="/cocon-sacre-2026"
                className="w-full sm:w-auto px-12 py-6 rounded-full bg-[#3F2A24] text-center transition-all duration-500 hover:bg-[#C5A059] group formula-card-premium"
                data-testid="hero-btn-sacre"
              >
                <span className="block text-sm tracking-[0.15em] uppercase text-[#C5A059] group-hover:text-[#3F2A24] mb-0.5 transition-colors">Bali & Java</span>
                <span className="block text-[11px] tracking-[0.1em] uppercase text-[#C5A059]/40 group-hover:text-[#3F2A24]/40 mb-1.5 transition-colors">Printemps 2026</span>
                <span className="block text-sm text-[#C5A059]/60 group-hover:text-[#3F2A24]/60 italic font-serif transition-colors">Une immersion entre terre, feu<br/>et transformation intérieure.</span>
              </Link>

              <Link
                to="/valouse"
                className="w-full sm:w-auto px-12 py-6 rounded-full bg-[#77574C] text-center hover:bg-[#C5A059] transition-all duration-500 group formula-card-premium"
                data-testid="hero-btn-valouse"
              >
                <span className="block text-sm tracking-[0.15em] uppercase text-[#F3E5CF] mb-0.5">Drôme Provençale</span>
                <span className="block text-[11px] tracking-[0.1em] uppercase text-[#F3E5CF]/40 mb-1.5">Rentrée 2026</span>
                <span className="block text-sm text-[#F3E5CF]/60 italic font-serif">Un retour au corps, au lien et à l'essentiel.</span>
              </Link>
            </div>
          </div>

          {/* Mantre */}
          <div
            ref={mantreReveal.ref}
            className={`scroll-reveal-slow ${mantreReveal.isVisible ? "visible" : ""}`}
          >
            <div className="text-[#C5A059]/40 text-2xl mb-14 opacity-0 animate-[fadeIn_2s_ease-out_0.5s_forwards]">—</div>
            <div className="space-y-3">
              <p className="font-serif text-lg sm:text-xl text-[#3F2A24]">
                Comprendre ne transforme pas.
              </p>
              <p className="font-serif text-lg sm:text-xl text-[#3F2A24] italic">
                Incarner, oui.
              </p>
              <div className="h-4" />
              <p className="font-serif text-base sm:text-lg text-[#3F2A24]/50 italic">
                Et cela ne se lit pas.
              </p>
              <p className="font-serif text-base sm:text-lg text-[#3F2A24]/50 italic">
                Cela se vit.
              </p>
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

/* ═══ CONSERVATION TEMPORAIRE — ancien HeroSection ═══
 *
 * Texte précédent du hero :
 *   "Certains lieux ne sont pas des destinations."
 *   "Ce sont des passages."
 *   COCON
 *   "Espaces de retraites & de transmission"
 *   "Comprendre ne transforme pas."
 *   "Incarner, oui."
 *   "Deux chemins s'ouvrent aujourd'hui."
 *   Boutons: COCON SACRÉ (Bali & Java) + COCON VALOUSE (Drôme Provençale)
 *
 * ═══ FIN CONSERVATION TEMPORAIRE ═══ */

export default HeroSection;
